import { Plus, Trash2, X } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";
import { EXAMS } from "../data/exams";
import type { ExamId } from "../data/types";
import { useLang, useT } from "../i18n";
import { pick } from "../i18n/lang";
import { api, ApiError, type AdminQuestionInput } from "../lib/api";

interface OptionDraft { en: string; vi: string; noteEn: string; noteVi: string; correct: boolean }

const emptyOption = (): OptionDraft => ({ en: "", vi: "", noteEn: "", noteVi: "", correct: false });
const MIN_OPTIONS = 2;
const MAX_OPTIONS = 6;

/** Thông báo lỗi từ API (mã trong server/src/questions.ts). */
const ERRORS: Record<string, { vi: string; en: string }> = {
  "bad-domain": { vi: "Domain không thuộc chứng chỉ đã chọn.", en: "Domain does not belong to the selected exam." },
  "scenario-required": { vi: "Chứng chỉ này thi theo kịch bản — phải chọn kịch bản.", en: "This exam is scenario-based — choose a scenario." },
  "bad-scenario": { vi: "Kịch bản không thuộc chứng chỉ đã chọn.", en: "Scenario does not belong to the selected exam." },
  "objective-domain-mismatch": { vi: "Objective không thuộc domain đã chọn.", en: "Objective does not belong to the selected domain." },
  "option-count-mismatch": { vi: "Số phương án tiếng Anh và tiếng Việt phải bằng nhau.", en: "English and Vietnamese option counts must match." },
  "duplicate-options": { vi: "Có phương án tiếng Anh bị trùng.", en: "Duplicate English options." },
  "bad-answer": { vi: "Chọn ít nhất 1 đáp án đúng và không được đánh dấu đúng tất cả.", en: "Mark at least one correct option, but not all of them." },
  "bad-sources": { vi: "Nguồn tham khảo phải có tiêu đề và URL https://.", en: "Sources need a title and an https:// URL." },
  "taxonomy-missing": { vi: "Máy chủ thiếu dữ liệu phân loại — cần build lại API.", en: "Server is missing taxonomy data — rebuild the API." },
};

export function AdminQuestionForm({ onCreated, onCancel }: { onCreated: (id: string) => void; onCancel: () => void }) {
  const t = useT();
  const lang = useLang();
  const [exam, setExam] = useState<ExamId>(EXAMS[0]!.id);
  const examDef = useMemo(() => EXAMS.find((e) => e.id === exam)!, [exam]);
  const [domain, setDomain] = useState(examDef.domains[0]!.id);
  const [scenario, setScenario] = useState("");
  const [objective, setObjective] = useState("");
  const [difficulty, setDifficulty] = useState<1 | 2 | 3>(2);
  const [enPrompt, setEnPrompt] = useState("");
  const [viPrompt, setViPrompt] = useState("");
  const [code, setCode] = useState("");
  const [options, setOptions] = useState<OptionDraft[]>(() => Array.from({ length: 4 }, emptyOption));
  const [enExpl, setEnExpl] = useState("");
  const [viExpl, setViExpl] = useState("");
  const [takeawayVi, setTakeawayVi] = useState("");
  const [takeawayEn, setTakeawayEn] = useState("");
  const [sources, setSources] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const scenarios = examDef.scenarios ?? [];
  const objectives = (examDef.taskStatements ?? []).filter((o) => o.domain === domain);

  // Đổi chứng chỉ → reset phân loại phụ thuộc để không gửi domain/kịch bản của đề khác.
  const changeExam = (id: ExamId) => {
    const next = EXAMS.find((e) => e.id === id)!;
    setExam(id);
    setDomain(next.domains[0]!.id);
    setScenario("");
    setObjective("");
  };
  const changeDomain = (id: string) => { setDomain(id); setObjective(""); };

  const setOption = (i: number, patch: Partial<OptionDraft>) => setOptions((os) => os.map((o, j) => (j === i ? { ...o, ...patch } : o)));
  const correctCount = options.filter((o) => o.correct).length;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");
    if (!correctCount || correctCount === options.length) return setError(pick(ERRORS["bad-answer"]!, lang));
    if (scenarios.length && !scenario) return setError(pick(ERRORS["scenario-required"]!, lang));
    const anyNote = options.some((o) => o.noteEn.trim() || o.noteVi.trim());
    if (anyNote && options.some((o) => !o.noteEn.trim() || !o.noteVi.trim())) {
      return setError(t("Đã nhập giải thích từng phương án thì phải nhập đủ cả EN và VI cho mọi phương án.", "If you add per-option notes, fill both EN and VI for every option."));
    }
    if (!!takeawayVi.trim() !== !!takeawayEn.trim()) return setError(t("Ghi nhớ cần cả bản EN và VI.", "Takeaway needs both EN and VI."));
    const parsedSources: { title: string; url: string }[] = [];
    for (const line of sources.split("\n").map((l) => l.trim()).filter(Boolean)) {
      const [title, url] = line.split("|").map((s) => s.trim());
      if (!title || !url || !url.startsWith("https://")) return setError(pick(ERRORS["bad-sources"]!, lang));
      parsedSources.push({ title, url });
    }

    const body: AdminQuestionInput = {
      exam, domain, difficulty, isPublic,
      ...(scenario ? { scenario } : {}),
      ...(objective ? { objective } : {}),
      answer: options.flatMap((o, i) => (o.correct ? [i] : [])),
      en: { prompt: enPrompt, options: options.map((o) => o.en), explanation: enExpl, ...(code.trim() ? { code } : {}) },
      vi: { prompt: viPrompt, options: options.map((o) => o.vi), explanation: viExpl },
      ...(anyNote ? { notes: { vi: options.map((o) => o.noteVi), en: options.map((o) => o.noteEn) } } : {}),
      ...(takeawayVi.trim() ? { takeaway: { vi: takeawayVi, en: takeawayEn } } : {}),
      ...(parsedSources.length ? { sources: parsedSources } : {}),
    };
    setSaving(true);
    try {
      const { question } = await api.adminCreateQuestion(body);
      onCreated(question.id);
    } catch (err) {
      const code = err instanceof ApiError ? err.message : "";
      setError(ERRORS[code] ? pick(ERRORS[code], lang) : t(`Không lưu được câu hỏi (${code || "lỗi mạng"}).`, `Could not save the question (${code || "network error"}).`));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="card admin-qform" onSubmit={(e) => void submit(e)}>
      <div className="row between">
        <h2 className="mb-0">{t("Thêm câu hỏi", "Add question")}</h2>
        <button type="button" className="icon-btn" onClick={onCancel} aria-label={t("Đóng", "Close")}><X size={18} /></button>
      </div>

      <fieldset>
        <legend>{t("Phân loại", "Classification")}</legend>
        <div className="admin-qform-grid">
          <label className="field"><span>{t("Chứng chỉ", "Exam")} *</span>
            <select value={exam} onChange={(e) => changeExam(e.target.value as ExamId)}>
              {EXAMS.map((x) => <option key={x.id} value={x.id}>{x.code} — {x.name}</option>)}
            </select>
          </label>
          <label className="field"><span>Domain *</span>
            <select value={domain} onChange={(e) => changeDomain(e.target.value)}>
              {examDef.domains.map((d) => <option key={d.id} value={d.id}>{d.name} ({d.weight}%)</option>)}
            </select>
          </label>
          {scenarios.length > 0 && (
            <label className="field"><span>{t("Kịch bản", "Scenario")} *</span>
              <select value={scenario} onChange={(e) => setScenario(e.target.value)} required>
                <option value="">{t("— Chọn kịch bản —", "— Choose a scenario —")}</option>
                {scenarios.map((s) => <option key={s.id} value={s.id}>{pick(s.title, lang)}</option>)}
              </select>
            </label>
          )}
          {objectives.length > 0 && (
            <label className="field"><span>Objective</span>
              <select value={objective} onChange={(e) => setObjective(e.target.value)}>
                <option value="">{t("— Không gắn —", "— None —")}</option>
                {objectives.map((o) => <option key={o.id} value={o.id}>{o.id} · {o.title}</option>)}
              </select>
            </label>
          )}
          <label className="field"><span>{t("Độ khó", "Difficulty")} *</span>
            <select value={difficulty} onChange={(e) => setDifficulty(Number(e.target.value) as 1 | 2 | 3)}>
              <option value={1}>1 · {t("Cơ bản", "Basic")}</option>
              <option value={2}>2 · {t("Trung bình", "Intermediate")}</option>
              <option value={3}>3 · {t("Khó", "Advanced")}</option>
            </select>
          </label>
        </div>
        <p className="tiny muted mb-0">{pick(examDef.domains.find((d) => d.id === domain)!.summary, lang)}</p>
      </fieldset>

      <fieldset>
        <legend>{t("Đề bài", "Prompt")}</legend>
        <div className="grid grid-2">
          <label className="field"><span>{t("Tiếng Anh (hiển thị như đề thật)", "English (shown as in the real exam)")} *</span>
            <textarea className="prose" rows={4} value={enPrompt} onChange={(e) => setEnPrompt(e.target.value)} required maxLength={4000} />
          </label>
          <label className="field"><span>{t("Tiếng Việt (mở sau khi trả lời)", "Vietnamese (revealed after answering)")} *</span>
            <textarea className="prose" rows={4} value={viPrompt} onChange={(e) => setViPrompt(e.target.value)} required maxLength={4000} />
          </label>
        </div>
        <label className="field mt"><span>{t("Đoạn code/cấu hình minh họa (không bắt buộc)", "Code/config snippet (optional)")}</span>
          <textarea rows={4} value={code} onChange={(e) => setCode(e.target.value)} maxLength={6000} />
        </label>
      </fieldset>

      <fieldset>
        <legend>{t("Phương án", "Options")} · {correctCount > 1 ? t(`chọn ${correctCount} đáp án`, `select ${correctCount}`) : t("một đáp án đúng", "single answer")}</legend>
        <div className="admin-qform-options">
          {options.map((o, i) => (
            <div key={i} className={`admin-qform-option${o.correct ? " correct" : ""}`}>
              <div className="row between">
                <label className="row"><input type="checkbox" checked={o.correct} onChange={(e) => setOption(i, { correct: e.target.checked })} /> <strong>{String.fromCharCode(65 + i)}</strong> {t("Đáp án đúng", "Correct")}</label>
                <button type="button" className="btn sm" disabled={options.length <= MIN_OPTIONS} onClick={() => setOptions((os) => os.filter((_, j) => j !== i))} aria-label={t(`Xóa phương án ${String.fromCharCode(65 + i)}`, `Remove option ${String.fromCharCode(65 + i)}`)}><Trash2 size={14} /></button>
              </div>
              <div className="grid grid-2">
                <input type="text" value={o.en} onChange={(e) => setOption(i, { en: e.target.value })} placeholder="English *" required maxLength={1000} aria-label={`${String.fromCharCode(65 + i)} English`} />
                <input type="text" value={o.vi} onChange={(e) => setOption(i, { vi: e.target.value })} placeholder="Tiếng Việt *" required maxLength={1000} aria-label={`${String.fromCharCode(65 + i)} Tiếng Việt`} />
                <input type="text" value={o.noteEn} onChange={(e) => setOption(i, { noteEn: e.target.value })} placeholder={t("Vì sao đúng/sai (EN)", "Why right/wrong (EN)")} maxLength={2000} aria-label={`${String.fromCharCode(65 + i)} note EN`} />
                <input type="text" value={o.noteVi} onChange={(e) => setOption(i, { noteVi: e.target.value })} placeholder={t("Vì sao đúng/sai (VI)", "Why right/wrong (VI)")} maxLength={2000} aria-label={`${String.fromCharCode(65 + i)} note VI`} />
              </div>
            </div>
          ))}
        </div>
        <button type="button" className="btn sm mt" disabled={options.length >= MAX_OPTIONS} onClick={() => setOptions((os) => [...os, emptyOption()])}><Plus size={14} /> {t("Thêm phương án", "Add option")}</button>
      </fieldset>

      <fieldset>
        <legend>{t("Giải thích", "Explanation")}</legend>
        <div className="grid grid-2">
          <label className="field"><span>English *</span><textarea className="prose" rows={4} value={enExpl} onChange={(e) => setEnExpl(e.target.value)} required maxLength={6000} /></label>
          <label className="field"><span>Tiếng Việt *</span><textarea className="prose" rows={4} value={viExpl} onChange={(e) => setViExpl(e.target.value)} required maxLength={6000} /></label>
          <label className="field"><span>{t("Ghi nhớ (EN)", "Takeaway (EN)")}</span><input type="text" value={takeawayEn} onChange={(e) => setTakeawayEn(e.target.value)} maxLength={500} /></label>
          <label className="field"><span>{t("Ghi nhớ (VI)", "Takeaway (VI)")}</span><input type="text" value={takeawayVi} onChange={(e) => setTakeawayVi(e.target.value)} maxLength={500} /></label>
        </div>
        <label className="field mt"><span>{t("Nguồn chính thức — mỗi dòng: Tiêu đề | https://…", "Official sources — one per line: Title | https://…")}</span>
          <textarea className="prose" rows={3} value={sources} onChange={(e) => setSources(e.target.value)} placeholder="Tool use overview | https://docs.claude.com/en/docs/agents-and-tools/tool-use/overview" />
        </label>
      </fieldset>

      <label className="row"><input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} /> {t("Công khai cho khách (không cần đăng nhập)", "Public to guests (no sign-in required)")}</label>

      {error && <div className="callout bad mt" role="alert">{error}</div>}
      <div className="row mt">
        <button type="submit" className="btn primary" disabled={saving}>{saving ? t("Đang lưu…", "Saving…") : t("Lưu câu hỏi", "Save question")}</button>
        <button type="button" className="btn" onClick={onCancel} disabled={saving}>{t("Hủy", "Cancel")}</button>
      </div>
    </form>
  );
}
